import { useState } from 'react';
import { 
  Phone, 
  Mail, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  Target,
  Calculator,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { formatCurrency, getBuyerDemandText, getMethodExplanation } from '../lib/calculator';

// Color logo for light backgrounds, light logo for dark/colored backgrounds
const BONZA_LOGO_COLOR = "https://customer-assets.emergentagent.com/job_2b31ab0e-18bf-40dd-b209-ee04ef532201/artifacts/pjhpmexv_Colour%20Landscape%20Logo%20-%20Large%20file%20size.png";
const BONZA_LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_value-finder-au/artifacts/g4de4ts7_Grey%20Landscape%20Logo.png";

const DemandBadge = ({ category }) => {
  const badgeClass = {
    'High': 'bonza-demand-badge high',
    'Steady': 'bonza-demand-badge steady',
    'Specialised': 'bonza-demand-badge specialised'
  }[category] || 'bonza-demand-badge steady';
  
  const Icon = {
    'High': TrendingUp,
    'Steady': Users,
    'Specialised': Target
  }[category] || Users;
  
  return (
    <span className={badgeClass}>
      <Icon className="w-4 h-4" />
      {category} Demand
    </span>
  );
};

// Get business type description based on industry/subcategory
const getBusinessTypeDescription = (industry, subcategory) => {
  if (subcategory) {
    const subcatLower = subcategory.toLowerCase();
    if (subcatLower.includes('cafe')) return 'cafe';
    if (subcatLower.includes('restaurant')) return 'restaurant';
    if (subcatLower.includes('hair')) return 'hair salon';
    if (subcatLower.includes('beauty')) return 'beauty salon';
    if (subcatLower.includes('gym') || subcatLower.includes('fitness')) return 'fitness business';
    if (subcatLower.includes('cleaning')) return 'cleaning business';
    if (subcatLower.includes('electrical')) return 'electrical business';
    if (subcatLower.includes('plumbing')) return 'plumbing business';
    if (subcatLower.includes('accounting') || subcatLower.includes('bookkeeping')) return 'accounting practice';
    return subcategory.toLowerCase();
  }
  
  const industryLower = industry?.toLowerCase() || 'business';
  if (industryLower.includes('food') || industryLower.includes('hospitality')) return 'hospitality business';
  if (industryLower.includes('retail')) return 'retail business';
  if (industryLower.includes('ecommerce')) return 'ecommerce business';
  if (industryLower.includes('automotive')) return 'automotive business';
  if (industryLower.includes('beauty') || industryLower.includes('health')) return 'beauty/health business';
  if (industryLower.includes('building') || industryLower.includes('trade')) return 'trades business';
  if (industryLower.includes('professional')) return 'professional services business';
  if (industryLower.includes('ndis')) return 'NDIS business';
  if (industryLower.includes('transport')) return 'transport business';
  if (industryLower.includes('accommodation') || industryLower.includes('tourism')) return 'accommodation business';
  
  return industryLower.replace(/\//g, '/');
};

// Generate factual buyer demand text based on actual business data
const getFactualDemandText = (demandCategory, formData, results) => {
  const businessType = getBusinessTypeDescription(formData.industry, formData.subcategory);
  const isProfitable = parseFloat(formData.ownerEarnings) > 0;
  const hasGoodFitout = parseFloat(formData.plantEquipment) >= 50000;
  const ownerEarnings = parseFloat(formData.ownerEarnings) || 0;
  
  let profitDescription = '';
  if (isProfitable) {
    if (ownerEarnings > 200000) {
      profitDescription = `a highly profitable ${businessType}`;
    } else if (ownerEarnings > 100000) {
      profitDescription = `a profitable ${businessType}`;
    } else {
      profitDescription = `a ${businessType} generating positive returns`;
    }
  } else if (hasGoodFitout) {
    profitDescription = `a turnkey opportunity with great fit-out`;
  } else {
    profitDescription = `a ${businessType} opportunity`;
  }
  
  switch (demandCategory) {
    case 'High':
      return `There is strong buyer demand for ${businessType}s in Australia. As ${profitDescription}, your business is well-positioned to attract significant enquiry and competitive buyer interest.`;
    case 'Steady':
      return `There is consistent buyer demand for ${businessType}s. As ${profitDescription}, serious purchasers actively searching in this sector are likely to show genuine interest.`;
    case 'Specialised':
      return `${businessType.charAt(0).toUpperCase() + businessType.slice(1)}s attract focused buyers who are typically highly motivated and knowledgeable. As ${profitDescription}, the specialised nature often leads to strong outcomes due to higher barriers to entry.`;
    default:
      return '';
  }
};

export const ResultsPage = ({ 
  formData,
  results,
  businessEnquiryId,
  onContactBroker,
  onEmailResult
}) => {
  const [showMethodDetails, setShowMethodDetails] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  const handleEmailResult = async () => {
    setEmailSending(true);
    try {
      await onEmailResult();
      setEmailSent(true);
    } catch (e) {
      console.error('Failed to send email', e);
    }
    setEmailSending(false);
  };
  
  return (
    <div className="bonza-results" data-testid="results-page">
      {/* Bonza Logo - using color logo on light background */}
      <div className="text-center mb-6">
        <img 
          src={BONZA_LOGO_COLOR} 
          alt="Bonza Business & Franchise Sales" 
          className="h-10 md:h-12 mx-auto"
          data-testid="results-bonza-logo"
        />
      </div>
      
      {/* Valuation Display */}
      <div className="bonza-valuation-display" data-testid="valuation-display">
        <p className="bonza-valuation-label">Your Estimated Market Value</p>
        <p className="bonza-valuation-amount" data-testid="valuation-range">
          {formatCurrency(results.valuationLow)} — {formatCurrency(results.valuationHigh)}
        </p>
        <p className="text-sm text-[#64748B] mt-3">
          Based on the information provided and current market positioning for businesses like yours.
        </p>
      </div>
      
      {/* Buyer Demand */}
      <div className="bg-white rounded-xl border border-[#E5E3EB] p-5 mb-6" data-testid="demand-section">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-semibold text-[#1A1523]">Buyer Demand</h3>
          <DemandBadge category={results.buyerDemandCategory} />
        </div>
        <p className="text-[#64748B] text-sm leading-relaxed">
          {getFactualDemandText(results.buyerDemandCategory, formData, results)}
        </p>
      </div>
      
      {/* How We Calculated */}
      <div className="bg-white rounded-xl border border-[#E5E3EB] p-5 mb-6" data-testid="calculation-section">
        <button
          type="button"
          onClick={() => setShowMethodDetails(!showMethodDetails)}
          className="w-full flex items-center justify-between text-left"
          data-testid="toggle-method-details"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F3F0F8] flex items-center justify-center">
              <Calculator className="w-5 h-5 text-[#5a2f92]" />
            </div>
            <h3 className="font-semibold text-[#1A1523]">How We Calculated This</h3>
          </div>
          {showMethodDetails ? (
            <ChevronUp className="w-5 h-5 text-[#64748B]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#64748B]" />
          )}
        </button>
        
        {showMethodDetails && (
          <div className="mt-4 pt-4 border-t border-[#E5E3EB]" data-testid="method-details">
            <p className="text-[#64748B] text-sm leading-relaxed mb-4">
              {getMethodExplanation(results.calculationMethod, {
                ...results,
                ownerEarnings: formData.ownerEarnings,
                annualRevenue: formData.annualRevenue,
                industry: formData.industry,
                plantEquipmentValue: formData.plantEquipment
              })}
            </p>
            
            {results.alternativeValues && (
              <div className="bg-[#F8F9FA] rounded-lg p-4 mt-3">
                <p className="text-sm font-medium text-[#1A1523] mb-2">Alternative Valuation:</p>
                {results.alternativeValues.revenueBased && (
                  <p className="text-sm text-[#64748B]" data-testid="alt-revenue-based">
                    Revenue-based: {formatCurrency(results.alternativeValues.revenueBased.low)} — {formatCurrency(results.alternativeValues.revenueBased.high)}
                  </p>
                )}
                {results.alternativeValues.profitBased && (
                  <p className="text-sm text-[#64748B]" data-testid="alt-profit-based">
                    Profit multiple-based: {formatCurrency(results.alternativeValues.profitBased.low)} — {formatCurrency(results.alternativeValues.profitBased.high)}
                  </p>
                )}
                {results.alternativeValues.peMethod && (
                  <p className="text-sm text-[#64748B]">
                    P&E + Earnings: {formatCurrency(results.alternativeValues.peMethod)}
                  </p>
                )}
              </div>
            )}
            
            {/* Adjustments breakdown */}
            {Object.values(results.adjustmentsApplied).some(v => v > 0) && (
              <div className="mt-4">
                <p className="text-sm font-medium text-[#1A1523] mb-2">Your market price was adjusted upwards to reflect:</p>
                <ul className="text-sm text-[#64748B] space-y-1">
                  {results.adjustmentsApplied.lease > 0 && (
                    <li>• Strong lease security (+{(results.adjustmentsApplied.lease * 100).toFixed(0)}%)</li>
                  )}
                  {results.adjustmentsApplied.revenueTrend > 0 && (
                    <li>• Positive revenue trend (+{(results.adjustmentsApplied.revenueTrend * 100).toFixed(0)}%)</li>
                  )}
                </ul>
              </div>
            )}
            
            {/* Negative adjustments */}
            {Object.values(results.adjustmentsApplied).some(v => v < 0) && (
              <div className="mt-4">
                <p className="text-sm font-medium text-[#1A1523] mb-2">Your market price was adjusted to reflect:</p>
                <ul className="text-sm text-[#64748B] space-y-1">
                  {results.adjustmentsApplied.revenueTrend < 0 && (
                    <li>• Current revenue trend ({(results.adjustmentsApplied.revenueTrend * 100).toFixed(0)}%)</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Business Strengths */}
      <div className="bg-white rounded-xl border border-[#E5E3EB] p-5 mb-6" data-testid="strengths-section">
        <h3 className="font-semibold text-[#1A1523] mb-4">Key Strengths of Your Business</h3>
        <ul className="bonza-strengths-list">
          {results.businessStrengths.map((strength, index) => (
            <li key={index}>
              <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
              <span className="text-[#1A1523] text-sm">{strength}</span>
            </li>
          ))}
        </ul>
      </div>
      
      {/* CTA Section */}
      <div className="bonza-cta-section" data-testid="cta-section">
        <h3 className="text-xl font-semibold text-[#1A1523] mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Ready to Sell Your Business?
        </h3>
        <p className="text-[#64748B] text-sm leading-relaxed mb-6 max-w-lg mx-auto">
          Bonza sells all types of businesses from $50,000 to $3,000,000, with a market leading 4% commission. 
          We look forward to helping you go from selling to sold!
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            data-testid="contact-broker-btn"
            className="bonza-btn-primary w-full sm:w-auto h-12 px-6 text-base font-semibold rounded-xl"
            asChild
          >
            <a href="https://bonzabfs.com.au/success-stories" target="_blank" rel="noopener noreferrer">
              Recent Sales
            </a>
          </Button>
          
          <Button
            onClick={handleEmailResult}
            disabled={emailSending || emailSent}
            variant="outline"
            data-testid="email-result-btn"
            className="w-full sm:w-auto h-12 px-6 text-base rounded-xl border-[#5a2f92] text-[#5a2f92] hover:bg-[#F3F0F8]"
          >
            <Mail className="w-4 h-4 mr-2" />
            {emailSent ? 'Email Sent!' : emailSending ? 'Sending...' : 'Email Me My Result'}
          </Button>
        </div>
        
        <p className="text-sm text-[#64748B] mt-4">
          No obligation. Just clear advice from an experienced small-business sales team.
        </p>
      </div>
      
      {/* Business Enquiry ID - for CRM/tracking */}
      {businessEnquiryId && (
        <div 
          className="text-center text-xs text-[#94A3B8] mt-6 mb-2" 
          data-testid="business-enquiry-id"
          data-enquiry-id={businessEnquiryId}
        >
          Reference: {businessEnquiryId}
        </div>
      )}
      
      {/* Disclaimer */}
      <p className="bonza-disclaimer" data-testid="disclaimer">
        This estimate is a guide only based on the information provided. Actual sale prices 
        can vary depending on buyer demand, deal structure, location and how the business 
        is taken to market.
      </p>
    </div>
  );
};
